/**
 * Bubble.js
 * The main bubble object
 */

import * as PIXI from "pixi.js";
import {
    Game,
    DesignDataTypes,
    GameAnimatedSprite,
    GameTween,
    GameScene,
    GameSprite,
    GameText,
    GameDisplayContainer,
    GameTimer
} from "pixi_core";

import { Common } from "./Common";

import {
    GameEvents,
    SceneLayers,
    BubbleColor,
    BubblePool,
    BubblePopSource,
    BUBBLE_COLOR_ID_TO_GRAPHIC,
    DEFAULT_BLOOMING_COUNTDOWN,
    DEFAULT_STONE_BLOOMING_COUNTDOWN,
    DEFAULT_STONE_BLOOMING_RADIUS,
    DEFAULT_BLOOMING_RADIUS,
    CHAMELEON_BUBBLE_IN_GAME_OVERLAY,
    MULTICOLOR_BUBBLE_IN_GAME_OVERLAY,
    DEFAULT_HEAL_BUBBLE_COUNTDOWN,
    DEFAULT_BLOOMING_NBR,
    DEFAULT_STONE_BLOOMING_NBR,
    PIERCE_BUBBLE_IDLE_OVERLAY,
    PIERCE_BUBBLE_TRANSITION_OVERLAY,
    PIERCE_BUBBLE_TRAIL_OVERLAY,
    DEFAULT_SPAWN_INITIAL_SCALE,
    HEAL_BUBBLE_IDLE_OVERLAY,
    NB_COLORED_BUBBLES,
    MULTICOLOR_BUBBLE_TRANSITION_OVERLAY,
    MULTICOLOR_BUBBLE_TRAIL_OVERLAY,
    SHIELD_BUBBLE_OVERLAY,
    LIGHTNING_BUBBLE_OVERLAY,
    LIGHTNING_BUBBLE_TRAIL_ANCHOR_X,
    BURST_BUBBLE_IN_GAME_OVERLAY_1,
    BURST_BUBBLE_IN_GAME_OVERLAY_2,
    BURST_BUBBLE_TRAIL_OVERLAY,
    BURST_BUBBLE_TRANSITION_OVERLAY,
    BLOOMING_BUBBLE_ANIMATED_OVERLAY,
    BLOOMING_BUBBLE_APPEAR_ANIMATION,
    STONE_BLOOMING_BUBBLE_ANIMATED_OVERLAY,
    STONE_BLOOMING_BUBBLE_APPEAR_ANIMATION,
    BUBBLE_COLOR_ID_TO_NAME
} from "../Constants";

export class Bubble {
    /** @private @type {Number} */
    static _lastSpawnTime = 0;

    /**
     * @public
     * @param {Object} position - Position x, y
     * @param {BubbleColor} color - Visual whatever the type
     * @param {BubbleColor} bubbleSpecialsType
     * @param {BubblePool} bubblePool
     * @param {GameScene} scene
     * @param {Object} gridPos
     * @param {Boolean} countdownStarted
     */
    constructor(position, color, bubbleSpecialsType, bubblePool, scene, gridPos = null, countdownStarted = false) {
        /** @private @type {import("../Constants").AnimationSettings} */
        this._settings = Common.settings.animations;

        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {BubbleColor} */
        this._color = color;

        /** @private @type {BubbleColor} */
        this._specialType = bubbleSpecialsType;

        /** @private @type {BubbleColor} */
        this._futureColor = BubbleColor.eUNDEFINED;

        /** @private @type {BubbleColor} */
        this._futureSpecialType = BubbleColor.eUNDEFINED;

        /** @private @type {BubblePool} */
        this._bubblePool = bubblePool;

        /** @private @type {Object} */
        this._gridPos = { row: -1, column: -1 };

        if (gridPos) {
            this._gridPos = gridPos;
        }

        /** @private @type {GameSprite} */
        this._underlay = null;

        /** @private @type {GameSprite} */
        this._underlayBurst = null;

        /** @public @type {GameDisplayContainer} */
        this._root = new GameDisplayContainer();

        this._scene.addChild(this._root, SceneLayers.eBUBBLES);

        /** @private @type {GameSprite} */
        this._graphic = null;

        /** @private @type {GameSprite} */
        this._overlay = null;

        /** @private @type {GameSprite} */
        this._cannonOutline = null;

        /** @private @type {GameAnimatedSprite} */
        this._cannonSpin = null;

        /** @private @type {GameAnimatedSprite} */
        this._colorChangeOverlay = null;

        /** @private @type {GameAnimatedSprite} */
        this._animatedUnderlay = null;

        /** @private @type {GameAnimatedSprite} */
        this._animatedOverlay = null;

        /** @private @type {GameText} */
        this._countdownLabel = null;

        /** @private @type {Number} */
        this._bubbleAngle = 0;

        /** @private @type {Number} */
        this._countdown = 0;

        /** @private @type {Number} */
        this._currentCountdown = 0;

        /** @public @type {Number} - Blooming bubbles */
        this.bloomingRadius = 0;

        /** @public @type {Number} - Blooming bubbles */
        this.bloomingNbr = 0;

        /** @public @type {Number} - Pierce bubble counter (Captain Marvel) */
        this.pierceCount = 0;

        /** @private @type {Number} */
        this._x = position.x;
        /** @private @type {Number} */
        this._y = position.y;

        this._root.x = this._x;
        this._root.y = this._y;

        const animationData = Game.design.getGraphicData("BubblePop");

        /** @private @type {GameAnimatedSprite} */
        this._popAnimation = new GameAnimatedSprite(null, animationData);
        this._root.addChild(this._popAnimation);
        this._popAnimation.visible = false;

        /** @private @type {GameTween} */
        this._rotationTween = null;
        /** @private @type {GameTween} */
        this._antiRotationTween = null;
        /** @private @type {GameTimer} */
        this._timerScatterDelay = null;
        /** @private @type {GameTimer} */
        this._timerPopDelay = null;
        /** @private @type {GameTween} */
        this._scatterMovementTween = null;
        /** @private @type {GameTween} */
        this._spawnTween = null;

        /** @public @type {Boolean} */
        this.isPopping = false;

        /** @public @type {Number} */
        this.poppingDelay = 0;

        /** @private @type {Boolean} */
        this._countdownStarted = countdownStarted;

        this._setColor(this._color);

        /** @public @type {Boolean} - Used by the match detection algorithm */
        this.processed = false;
        /** @public @type {Boolean} - Used by the disconnection algorithm */
        this.connected = false;
        /** @public @type {Boolean} - Used by the random generation algorithm */
        this.initialMatched = false;
        /** @public @type {Number} - Used by the random generation algorithm */
        this.initialClusterSize = 0;
        /** @public @type {Boolean} - Can it count down? */
        this.canCountdown = true;

        Game.on(GameEvents.eGAME_BUBBLE_DELAY_COUNTDOWN, this._onDelayCountdown, this);
    }

    /**
     * This bubble is spawned from a nearby blooming bubble
     * Trigger the spawn bloom bubble animation (light thingies)
     * @public
     * @param {Bubble} bloomingBubble
     * @param {Function} [callback] - Completion callback
     */
    bloomSpawn(bloomingBubble, callback) {
        const appearAnimationId =
            bloomingBubble.color === BubbleColor.eBLOOMING ? BLOOMING_BUBBLE_APPEAR_ANIMATION : STONE_BLOOMING_BUBBLE_APPEAR_ANIMATION;

        this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(appearAnimationId));
        this._root.addChild(this._animatedOverlay);
        this._graphic.visible = false;
        this._animatedOverlay.playAnimation("appear");
        this._animatedOverlay.onComplete = () => {
            this._animatedOverlay.destroy();
            this._animatedOverlay = null;
            Game.audio.playSfx("IDSND_BUB_STONE_SPAWN");
            this.spawn(callback);
        };
    }

    /**
     * Trigger the spawn bubble animation (scale-up)
     * @public
     * @param {Function} [callback] - Completion callback
     * @param {Number} [targetScale]
     * @param {Boolean} [spawnInCannon]
     * @param {Boolean} [ignoreCascadeDelay]
     */
    spawn(callback = null, targetScale = 1, spawnInCannon = false, ignoreCascadeDelay = false) {
        const currentTime = Date.now();

        let cascadeDelay = this._settings.spawnCascadeDelay;

        // Reduce the cascade delay as more and more bubble are being spawned at the same time
        const currentWaitTime = Bubble._lastSpawnTime - currentTime;
        if (currentWaitTime > 0) {
            const powerFactor = Math.round(currentWaitTime / cascadeDelay);
            if (powerFactor > 0) {
                const speedUpFactor = Math.pow(1 - this._settings.spawnCascadeSpeedUp, powerFactor);
                cascadeDelay = cascadeDelay * speedUpFactor;
            }
        }

        if (ignoreCascadeDelay === true) {
            cascadeDelay = 0;
        }

        const nextSpawnTime = Math.max(currentTime, Bubble._lastSpawnTime + cascadeDelay);
        const delay = nextSpawnTime - currentTime;

        this._graphic.visible = true;
        this._root.scale.x = DEFAULT_SPAWN_INITIAL_SCALE;
        this._root.scale.y = DEFAULT_SPAWN_INITIAL_SCALE;
        this._root.visible = false;

        if (spawnInCannon && this._color < BubbleColor.eSPECIAL) {
            this._cannonOutline = new GameSprite("IMG_CanonBubbleStroke");
            this._root.addChild(this._cannonOutline);
        }

        if (this._spawnTween) {
            this._spawnTween.destroy();
            this._spawnTween = null;
        }

        this._spawnTween = Game.windows
            .getCurrentWindow()
            .createTween(this._root.scale)
            .to({ x: targetScale, y: targetScale }, this._settings.spawnDuration)
            .delay(delay)
            .easing(GameTween.Easing.ElasticOut)
            .onStart(() => {
                this._root.visible = true;
            })
            .onComplete(() => {
                if (this._spawnTween) {
                    this._spawnTween.destroy();
                    this._spawnTween = null;
                }

                if (callback) callback();
            })
            .start();

        Bubble._lastSpawnTime = nextSpawnTime;
    }

    /**
     * @public
     */
    showCannonSpin() {
        // Ignore for special bubbles
        if (this._color < BubbleColor.eSPECIAL && !this._cannonSpin) {
            this._cannonSpin = new GameAnimatedSprite(null, Game.design.getGraphicData("CannonBubble"));
            this._cannonSpin.playAnimation(BUBBLE_COLOR_ID_TO_NAME[this._color], true);
            this._setGraphicsOrder();
        }
    }

    /**
     * @public
     */
    hideCannonSpin() {
        if (this._cannonSpin) {
            this._cannonSpin.destroy();
            this._cannonSpin = null;
        }
    }

    /**
     * @public
     * @returns {Number}
     */
    get scale() {
        return this._root.scale.x;
    }

    /**
     * @public
     * @param {Number} value
     */
    set scale(value) {
        if (this._root) {
            this._root.scale.x = value;
            this._root.scale.y = value;
        }
    }

    /**
     * @public
     * @returns {Number}
     */
    get x() {
        return this._x;
    }

    /**
     * @public
     * @param {Number} x
     */
    set x(x) {
        this._x = x;
        this._root.x = x;
    }

    /**
     * @public
     * @returns {Number}
     */
    get y() {
        return this._y;
    }

    /**
     * @public
     * @param {Number} y
     */
    set y(y) {
        this._y = y;
        this._root.y = y;
    }

    /**
     * @public
     * @returns {Object}
     */
    getPos() {
        return { x: this._x, y: this._y };
    }

    /**
     * @public
     * @param {Number} x
     * @param {Number} y
     */
    setPos(x, y) {
        this._x = x;
        this._y = y;

        this._root.x = x;
        this._root.y = y;
    }

    /**
     * @public
     * @param {Number} row
     * @param {Number} column
     */
    setGridPos(row, column) {
        this._gridPos.row = row;
        this._gridPos.column = column;
    }

    /**
     * @public
     * @returns {Object}
     */
    getGridPos() {
        return { row: this._gridPos.row, column: this._gridPos.column };
    }

    /**
     * @public
     * @returns {Number}
     */
    get column() {
        return this._gridPos.column;
    }

    /**
     * @public
     */
    set column(column) {
        this._gridPos.column = column;
    }

    /**
     * @public
     * @returns {Number}
     */
    get row() {
        return this._gridPos.row;
    }

    /**
     * @public
     */
    set row(row) {
        this._gridPos.row = row;
    }

    /**
     * @public
     * @returns {Boolean}
     */
    get visible() {
        return this._root ? this._root.visible : false;
    }

    /**
     * @public
     * @param {Boolean} visible
     */
    set visible(visible) {
        if (this._root) {
            this._root.visible = visible;
        }
    }

    /**
     * @public
     * @param {{color: BubbleColor, special: BubbleColor}} newColor
     * @param {Boolean} instant
     */
    changeColor(newColor, instant = false) {
        this._futureSpecialType = newColor.special >= BubbleColor.eSPECIAL ? newColor.special : BubbleColor.eUNDEFINED;
        this._futureColor = newColor.color;

        // Choose a random basic color [0 ... 5]
        if (this._futureColor === BubbleColor.eRANDOM || this._futureColor === BubbleColor.eRANDOM_EDITOR) {
            this._futureColor = Math.floor(Math.random() * NB_COLORED_BUBBLES);
        }

        if (instant) {
            this._specialType = this._futureSpecialType;
            this._setColor(this._futureColor);
            return;
        }

        Game.audio.playSfx("IDSND_BUB_CHANGE");
        this._colorChangeOverlay.visible = true;
        this._colorChangeOverlay.playAnimation("changeColor");
        this._colorChangeOverlay.onComplete = this._onFinishColorChange.bind(this);
    }

    /**
     * @private
     */
    _onFinishColorChange() {
        this._specialType = this._futureSpecialType;
        this._setColor(this._futureColor);
        this._colorChangeOverlay.playAnimation("changeColorEnd");
        this._colorChangeOverlay.onComplete = this._onFinishColorChangeAnimation.bind(this);
    }

    /**
     * @private
     */
    _onFinishColorChangeAnimation() {
        this._colorChangeOverlay.visible = false;
    }

    /**
     * @private
     * @param {BubbleColor} color
     */
    _setColor(color) {
        if (this._color !== color) {
            this._color = color;
        }

        if (this._rotationTween) {
            this._rotationTween.stop();
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        // Basic bubble graphic
        const bubbleColorData = Game.design.getData(BUBBLE_COLOR_ID_TO_GRAPHIC[this._color], DesignDataTypes.eGRAPHIC);
        const specialBubbles = Game.design.getData("SpecialBubbles", DesignDataTypes.eGLOBAL_ENTITY);
        const settings = Game.design.findEntityByName(specialBubbles, "SpecialBubbleSettings");
        this._scene.setObjectLayer(this._root, SceneLayers.eBUBBLES);

        if (this._graphic) {
            this._graphic.texture = PIXI.Texture.from(bubbleColorData.asset);
        } else {
            this._graphic = new GameSprite(bubbleColorData.asset);
        }

        this._graphic.anchor.x = 0.5;
        this._graphic.anchor.y = 0.5;
        this._graphic.angle = 0;

        if (this._specialType !== BubbleColor.eCHAMELEON && this._overlay) {
            this._overlay.destroy();
            this._overlay = null;
        }

        if (this._animatedUnderlay) {
            this._animatedUnderlay.destroy();
            this._animatedUnderlay = null;
        }

        if (this._animatedOverlay) {
            this._animatedOverlay.destroy();
            this._animatedOverlay = null;
        }

        if (this._countdownLabel) {
            this._countdownLabel.destroy();
            this._countdownLabel = null;
        }

        if (this._underlay) {
            this._underlay.destroy();
            this._underlay = null;
        }

        if (this._underlayBurst) {
            this._underlayBurst.destroy();
            this._underlayBurst = null;
        }

        // Multicolor underlay
        if (this._color === BubbleColor.eMULTICOLOR) {
            this.startMulticolorSpin();
        }

        // Multicolor underlay
        if (this._color === BubbleColor.eBURST) {
            this.startBurstSpin();
        }

        // Heal overlay
        if (this._specialType === BubbleColor.eHEAL) {
            this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(HEAL_BUBBLE_IDLE_OVERLAY));
            this._animatedOverlay.playAnimation("healIdle");

            const specialBubbles = Game.design.getData("SpecialBubbles", DesignDataTypes.eGLOBAL_ENTITY);
            const settings = Game.design.findEntityByName(specialBubbles, "SpecialBubbleSettings");

            this._countdown = Game.utils.assertNumber(settings.HealBubbleCountdown, DEFAULT_HEAL_BUBBLE_COUNTDOWN);
            this._currentCountdown = this._countdown;

            this._setLabel();
        }

        // Colorless overlay
        if (this._specialType === BubbleColor.eCOLORLESS) {
            const bubbleColorData = Game.design.getData(BUBBLE_COLOR_ID_TO_GRAPHIC[BubbleColor.eCOLORLESS], DesignDataTypes.eGRAPHIC);
            this._graphic.texture = PIXI.Texture.from(bubbleColorData.asset);
        }

        // Pierce overlay
        if (this._color === BubbleColor.ePIERCE) {
            this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(PIERCE_BUBBLE_IDLE_OVERLAY));
            this._animatedOverlay.playAnimation("pierceIdle");
        }

        // Thor/Lightning overlay
        if (this._color === BubbleColor.eLIGHTNING) {
            this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(LIGHTNING_BUBBLE_OVERLAY));
            this._animatedOverlay.playAnimation("Idle", true);
        }

        if (this._specialType === BubbleColor.eSHIELDED) {
            let self = this;
            Game.audio.playSfx("IDSND_BUB_SHIELD_OVERLAY");
            this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(SHIELD_BUBBLE_OVERLAY));
            this._animatedOverlay.playAnimation("shieldUp");
            this._animatedOverlay.onComplete = function () {
                self._animatedOverlay.playAnimation("shieldIdle");
                self._animatedOverlay.stop();
            };
        }

        // Chameleon overlay
        if (this._specialType === BubbleColor.eCHAMELEON && this._color < BubbleColor.eSPECIAL) {
            const bubbleOverlayData = Game.design.getData(CHAMELEON_BUBBLE_IN_GAME_OVERLAY, DesignDataTypes.eGRAPHIC);

            if (this._overlay) {
                this._overlay.texture = PIXI.Texture.from(bubbleOverlayData.asset);
            } else {
                this._overlay = new GameSprite(bubbleOverlayData.asset);
            }

            if (this._rotationTween) {
                this._rotationTween.stop();
                this._rotationTween.destroy();
                this._rotationTween = null;
            }

            this._overlay.angle = 0;
            this._rotationTween = Game.windows
                .getCurrentWindow()
                .createTween(this._overlay)
                .to({ angle: 360 }, Common.settings.chameleonBubbleSpinTime)
                .repeat(Infinity)
                .start();
        }

        if (!this._colorChangeOverlay) {
            const colorChangeAnimationData = Game.design.getGraphicData("BubbleChangeColor");
            this._colorChangeOverlay = new GameAnimatedSprite(null, colorChangeAnimationData);
            this._colorChangeOverlay.visible = false;
        }

        if (this._underlay) {
            this._underlay.anchor.x = 0.5;
            this._underlay.anchor.y = 0.5;
        }

        if (this._underlayBurst) {
            this._underlayBurst.anchor.x = 0.5;
            this._underlayBurst.anchor.y = 0.5;
        }

        if (this._overlay) {
            this._overlay.anchor.x = 0.5;
            this._overlay.anchor.y = 0.5;
        }

        if (this._colorChangeOverlay) {
            this._colorChangeOverlay.anchor.x = 0.5;
            this._colorChangeOverlay.anchor.y = 0.5;
        }

        // Set blooming settings
        if (
            (this._color === BubbleColor.eBLOOMING && this._specialType === BubbleColor.eBLOOMING) ||
            (this._color === BubbleColor.eSTONE_BLOOMING && this._specialType === BubbleColor.eSTONE_BLOOMING)
        ) {
            this._countdown = 0;
            if (this._specialType === BubbleColor.eBLOOMING) {
                this._countdown = Game.utils.assertNumber(settings.BloomingCountdown, DEFAULT_BLOOMING_COUNTDOWN);
                this.bloomingRadius = Game.utils.assertNumber(settings.BloomingRadius, DEFAULT_BLOOMING_RADIUS);
                this.bloomingNbr = Game.utils.assertNumber(settings.BloomingNbr, DEFAULT_BLOOMING_NBR);
            }
            if (this._specialType === BubbleColor.eSTONE_BLOOMING) {
                this._countdown = Game.utils.assertNumber(settings.StoneBloomingCountdown, DEFAULT_STONE_BLOOMING_COUNTDOWN);
                this.bloomingRadius = Game.utils.assertNumber(settings.StoneBloomingRadius, DEFAULT_STONE_BLOOMING_RADIUS);
                this.bloomingNbr = Game.utils.assertNumber(settings.StoneBloomingNbr, DEFAULT_STONE_BLOOMING_NBR);
            }
            this._currentCountdown = this._countdown;

            if (this._rotationTween) {
                this._rotationTween.stop();
                this._rotationTween.destroy();
                this._rotationTween = null;
            }

            if (this._color === BubbleColor.eBLOOMING) {
                this._rotationTween = Game.windows
                    .getCurrentWindow()
                    .createTween(this._graphic)
                    .to({ angle: 360 }, Common.settings.chameleonBubbleSpinTime)
                    .repeat(Infinity)
                    .start();
            }

            if (!this._animatedOverlay) {
                const animationId =
                    this._color === BubbleColor.eBLOOMING ? BLOOMING_BUBBLE_ANIMATED_OVERLAY : STONE_BLOOMING_BUBBLE_ANIMATED_OVERLAY;

                this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(animationId));
                this._animatedOverlay.visible = false;
            }

            this._setLabel();
        }

        Game.off(GameEvents.eGAME_TICKDOWN_COUNTDOWN, this._tickDownCountdown, this);

        if (
            (this._color === BubbleColor.eBLOOMING && this._specialType === BubbleColor.eBLOOMING) ||
            (this._color === BubbleColor.eSTONE_BLOOMING && this._specialType === BubbleColor.eSTONE_BLOOMING) ||
            this._specialType === BubbleColor.eHEAL
        ) {
            Game.on(GameEvents.eGAME_TICKDOWN_COUNTDOWN, this._tickDownCountdown, this);
        }

        this._setGraphicsOrder();
    }

    /**
     * Set the countdown label
     * @private
     */
    _setLabel() {
        //this._countdownStarted = false;
        this._countdownLabel = new GameText({
            text: String(this._currentCountdown),
            textStyle: "CountdownLabel"
        });

        this._countdownLabel.anchor.x = 0.5;
        this._countdownLabel.anchor.y = 0.5;
    }

    /**
     * Set the countdown label
     * @private
     */
    _setGraphicsOrder() {
        if (this._cannonSpin) this._root.addChild(this._cannonSpin);
        if (this._underlayBurst) this._root.addChild(this._underlayBurst);
        if (this._underlay) this._root.addChild(this._underlay);
        if (this._animatedUnderlay) this._root.addChild(this._animatedUnderlay);
        if (this._graphic) this._root.addChild(this._graphic);
        if (this._overlay) this._root.addChild(this._overlay);
        if (this._animatedOverlay) this._root.addChild(this._animatedOverlay);
        if (this._countdownLabel) this._root.addChild(this._countdownLabel);
        if (this._colorChangeOverlay) this._root.addChild(this._colorChangeOverlay);
        if (this._cannonOutline) this._root.addChild(this._cannonOutline);
    }

    /**
     * @public
     * @returns {BubbleColor}
     */
    get color() {
        return this._color;
    }

    /**
     * @public
     * @returns {BubbleColor}
     */
    get specialType() {
        return this._specialType;
    }

    /**
     * This bubble has just been fired from the cannon
     * @public
     * @param {Number} angle
     * @param {Boolean} fromCannonOrhitSidewall
     */
    onFired(angle, fromCannonOrhitSidewall) {
        if (this._cannonOutline) {
            this._cannonOutline.destroy();
            this._cannonOutline = null;
        }
        if (this._cannonSpin) {
            this._cannonSpin.destroy();
            this._cannonSpin = null;
        }

        if (this._color === BubbleColor.eMULTICOLOR || this._color === BubbleColor.eBURST) {
            this._underlay.visible = false;

            if (this._color === BubbleColor.eBURST) {
                this._underlayBurst.visible = false;
            }

            this._destroyTweens();
        }

        if (this._color === BubbleColor.eLIGHTNING) {
            if (!this._rotationTween) {
                const delay = (1000 * 360) / Common.settings.bubbles.thorRotationSpeed;
                this._rotationTween = Game.windows
                    .getCurrentWindow()
                    .createTween(this._graphic)
                    .to({ angle: 360 }, delay)
                    .repeat(Infinity)
                    .start();
            }
        }

        // TODO: The code below could be simplified

        if (
            (this._color === BubbleColor.ePIERCE || this._color === BubbleColor.eMULTICOLOR || this._color === BubbleColor.eBURST) &&
            fromCannonOrhitSidewall
        ) {
            this._bubbleAngle = angle;

            if (this._animatedOverlay) {
                this._animatedOverlay.stop();
                this._animatedOverlay.destroy();
                this._animatedOverlay = null;
            }

            if (this._animatedUnderlay) {
                this._animatedUnderlay.stop();
                this._animatedUnderlay.destroy();
                this._animatedUnderlay = null;
            }

            if (this._color === BubbleColor.ePIERCE) {
                this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(PIERCE_BUBBLE_TRANSITION_OVERLAY));
                this._animatedOverlay.angle = this._bubbleAngle;
                this._animatedOverlay.playAnimation("transition");
                this._animatedOverlay.onComplete = this._onStartTrailAnimation.bind(this);

                if (this._color === BubbleColor.ePIERCE) {
                    this._scene.setObjectLayer(this._root, SceneLayers.eBUBBLE_FIRED);
                }
            } else if (this._color === BubbleColor.eMULTICOLOR || this._color === BubbleColor.eBURST) {
                if (this._color === BubbleColor.eMULTICOLOR) {
                    this._animatedUnderlay = new GameAnimatedSprite(null, Game.design.getGraphicData(MULTICOLOR_BUBBLE_TRANSITION_OVERLAY));
                } else if (this._color === BubbleColor.eBURST) {
                    this._animatedUnderlay = new GameAnimatedSprite(null, Game.design.getGraphicData(BURST_BUBBLE_TRANSITION_OVERLAY));
                }

                this._animatedUnderlay.angle = this._bubbleAngle;
                this._animatedUnderlay.playAnimation("transition");
                this._animatedUnderlay.onComplete = this._onStartTrailAnimation.bind(this);
            }

            this._setGraphicsOrder();
        }

        if (this._color === BubbleColor.eLIGHTNING && fromCannonOrhitSidewall) {
            this._bubbleAngle = angle;

            this._animatedOverlay.playAnimation("Transition");
            // TODO: Remove when we can specify anchor as part of the animation
            this._animatedOverlay.anchor.x = LIGHTNING_BUBBLE_TRAIL_ANCHOR_X;
            this._animatedOverlay.angle = this._bubbleAngle;
            this._animatedOverlay.onComplete = this._onStartTrailAnimation.bind(this);
        }
    }

    /**
     * Starts the trail animation of the pierce bubble.
     * @private
     */
    _onStartTrailAnimation() {
        if (this._popAnimation.playing) {
            return;
        }

        if (this._color === BubbleColor.ePIERCE) {
            if (this._animatedOverlay) {
                this._animatedOverlay.destroy();
                this._animatedOverlay = null;
            }

            this._animatedOverlay = new GameAnimatedSprite(null, Game.design.getGraphicData(PIERCE_BUBBLE_TRAIL_OVERLAY));
            this._animatedOverlay.angle = this._bubbleAngle;
            this._animatedOverlay.playAnimation("trail");
        } else if (this._color === BubbleColor.eMULTICOLOR || this._color === BubbleColor.eBURST) {
            this._animatedUnderlay.destroy();
            this._animatedUnderlay = null;

            if (this._color === BubbleColor.eMULTICOLOR) {
                this._animatedUnderlay = new GameAnimatedSprite(null, Game.design.getGraphicData(MULTICOLOR_BUBBLE_TRAIL_OVERLAY));
            } else if (this._color === BubbleColor.eBURST) {
                this._animatedUnderlay = new GameAnimatedSprite(null, Game.design.getGraphicData(BURST_BUBBLE_TRAIL_OVERLAY));
            }
            this._animatedUnderlay.angle = this._bubbleAngle;
            this._animatedUnderlay.playAnimation("trail");
        } else if (this._color === BubbleColor.eLIGHTNING) {
            this._animatedOverlay.playAnimation("Trail");
            this._animatedOverlay.angle = this._bubbleAngle;
        }

        this._setGraphicsOrder();
    }

    /**
     * Pops the bubble after a match
     * @public
     * @param {Boolean} [bypassShield]
     * @param {BubblePopSource} [source]
     */
    pop(bypassShield = false, source) {
        this.isPopping = true;

        const playbackOptions = {
            overlap: true,
            overlapDelay: Common.settings.audio.bubbleBurstOverlapDelay,
            overlapMaxDelay: Common.settings.audio.bubbleBurstOverlapMaxDelay
        };

        // Sound fx
        if (source === BubblePopSource.ePIERCE) {
            Game.audio.playSfx("IDSND_BUB_PIERCE_EXPLO", playbackOptions);
        } else if (this._color < BubbleColor.eSPECIAL) {
            if (Math.random() < 0.5) {
                Game.audio.playSfx("IDSND_BUB_BURST_BASIC_01", playbackOptions);
            } else {
                Game.audio.playSfx("IDSND_BUB_BURST_BASIC_02", playbackOptions);
            }
        } else {
            Game.audio.playSfx("IDSND_BUB_BURST_SPECIAL", playbackOptions);
        }

        // Heal bubble effect
        if (this._specialType === BubbleColor.eHEAL) {
            Game.fire(GameEvents.eHEROES_HEAL_BUBBLE);
        }

        if (this._specialType === BubbleColor.eSHIELDED && !bypassShield) {
            this._popShield();
            return;
        }

        if (this._color === BubbleColor.eLIGHTNING && this._rotationTween) {
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        if (this._graphic) {
            this._scene.setObjectLayer(this._root, SceneLayers.eBUBBLE_OVER);
            this._graphic.destroy();
            this._graphic = null;

            if (this._overlay) {
                this._overlay.visible = false;
            }

            if (this._colorChangeOverlay) {
                this._colorChangeOverlay.visible = false;
            }

            if (this._underlay) {
                this._underlay.visible = false;
            }

            if (this._underlayBurst) {
                this._underlayBurst.visible = false;
            }

            if (this._animatedOverlay) {
                this._animatedOverlay.visible = false;
            }

            if (this._animatedUnderlay) {
                this._animatedUnderlay.visible = false;
            }

            if (this._countdownLabel) {
                this._countdownLabel.destroy();
                this._countdownLabel = null;
            }

            if (this._rotationTween) {
                this._rotationTween.stop();
                this._rotationTween.destroy();
                this._rotationTween = null;
            }

            let self = this;
            this._popAnimation.visible = true;

            if (source === BubblePopSource.ePIERCE) {
                this._popAnimation.playAnimation("piercePop");
            } else if (this._color === BubbleColor.eBURST) {
                this._popAnimation.playAnimation("burstPop");
            } else if (source === BubblePopSource.eLIGHTNING) {
                this._popAnimation.playAnimation("electricPop");
            } else if (this._color === BubbleColor.eLIGHTNING) {
                this._popAnimation.playAnimation("thorPop");
            } else if (this._color === BubbleColor.eMULTICOLOR) {
                this._popAnimation.playAnimation("rainbowPop");
            } else {
                this._popAnimation.playAnimation("pop");
            }

            this._popAnimation.onComplete = function () {
                Game.fire(GameEvents.eBUBBLE_POPPED, self);
            };
        }
    }

    /**
     * Drops/scatter the bubble after it is disconnected
     * @public
     * @param {{x: Number, y: Number}} contactPosition
     * @param {Boolean} requiresDamageTrail
     * @param {Number} timer
     */
    drop(contactPosition, requiresDamageTrail, timer) {
        if (this._rotationTween) {
            this._rotationTween.stop();
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        // Default scatter direction
        let direction = Math.PI;

        // Default scatter trajectory and delay
        let targetX = this._x - this._settings.scatterDistance;
        let targetY = this._y;
        let scatterDelay = this._settings.scatterDelay;

        // Calculate scatter trajectory consideting contact position
        if (contactPosition) {
            const deltaX = this._x - contactPosition.x;
            const deltaY = this._y - contactPosition.y;
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            // Make sure direction angle is a positive value
            direction = Math.atan2(deltaY, deltaX);
            if (direction < 0) {
                direction += Math.PI * 2;
            }

            scatterDelay = distance * this._settings.scatterDelayMultiplier;
        }

        // Apply min/max angle
        if (direction < this._settings.scatterDirectionMin) direction = this._settings.scatterDirectionMin;
        if (direction > this._settings.scatterDirectionMax) direction = this._settings.scatterDirectionMax;

        // Randomize direction
        let randomFactor = -1 + Math.random() * 2;
        direction += randomFactor * this._settings.scatterDirectionRandomRange;

        // Randomize movement distance
        let movementDistance = this._settings.scatterDistance;
        randomFactor = -1 + Math.random() * 2;
        movementDistance += randomFactor * this._settings.scatterDistanceRandomRange;

        targetX = this._x + Math.cos(direction) * movementDistance;
        targetY = this._y + Math.sin(direction) * movementDistance;

        if (this._timerScatterDelay) {
            this._timerScatterDelay.destroy();
            this._timerScatterDelay = null;
        }

        this._timerScatterDelay = Game.windows.getCurrentWindow().createTimer(scatterDelay);
        this._timerScatterDelay.onComplete = () => {
            this._scatter(targetX, targetY, this._settings.scatterDuration, requiresDamageTrail);
        };
    }

    /**
     * @public
     */
    stopAnimations() {
        if (this._animatedUnderlay && this._color === BubbleColor.eMULTICOLOR) {
            this._animatedUnderlay.stop();
            this._animatedUnderlay.visible = false;
            this._destroyTweens();
        }
    }

    /**
     * @private
     */
    _popShield() {
        let self = this;
        Game.audio.playSfx("IDSND_BUB_SHIELD_OV_DESPAWN");
        this._animatedOverlay.playAnimation("pop");
        this._animatedOverlay.onComplete = function () {
            self._specialType = BubbleColor.eNONE;
            self._animatedOverlay.visible = false;
            self._animatedOverlay.destroy();
            self._animatedOverlay = null;
        };
    }

    /**
     * @private
     * @param {Number} targetX
     * @param {Number} targetY
     * @param {Number} targetX
     * @param {Boolean} requiresDamageTrail
     */
    _scatter(targetX, targetY, duration, requiresDamageTrail) {
        this._scene.setObjectLayer(this._root, SceneLayers.eBUBBLES_DROPPED);

        if (this._timerScatterDelay) {
            this._timerScatterDelay.destroy();
            this._timerScatterDelay = null;
        }

        if (this._timerPopDelay) {
            this._timerPopDelay.destroy();
            this._timerPopDelay = null;
        }

        if (this._scatterMovementTween) {
            this._scatterMovementTween.destroy();
            this._scatterMovementTween = null;
        }

        this._scatterMovementTween = Game.windows
            .getCurrentWindow()
            .createTween(this)
            .to({ x: targetX, y: targetY }, duration)
            .easing(GameTween.Easing.CircularOut)
            .onComplete(() => {
                if (this._scatterMovementTween) {
                    this._scatterMovementTween.destroy();
                    this._scatterMovementTween = null;
                }
            })
            .start();

        this._timerPopDelay = Game.windows.getCurrentWindow().createTimer(this._settings.scatterPopDelay);
        this._timerPopDelay.onComplete = () => {
            this.pop(true, BubblePopSource.eDISCONNECT);
            if (requiresDamageTrail) {
                Game.fire(GameEvents.eBUBBLE_SCATTER_DONE, this);
            }
        };
    }

    /**
     * @public
     */
    popLastRow() {
        this.pop(true);
    }

    /**
     * Set the popping delay for a bubble that's waiting to pop in a match
     * @public
     */
    setPoppingDelay(poppingDelay) {
        this.poppingDelay = poppingDelay;
        this.isPopping = false;
    }

    /**
     * Start a multicolor bubble's spinning idle animation.
     * @public
     */
    startMulticolorSpin() {
        if (!this._underlay) {
            const bubbleUnderlayData = Game.design.getData(MULTICOLOR_BUBBLE_IN_GAME_OVERLAY, DesignDataTypes.eGRAPHIC);
            this._underlay = new GameSprite(bubbleUnderlayData.asset);
        }

        this._underlay.angle = 0;
        this._underlay.visible = true;

        if (this._rotationTween) {
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        this._rotationTween = Game.windows
            .getCurrentWindow()
            .createTween(this._underlay)
            .to({ angle: 360 }, Common.settings.multicolorBubbleSpinTime)
            .repeat(Infinity)
            .start();
    }

    /**
     * Start a burst bubble's spinning idle animation.
     * @public
     */
    startBurstSpin() {
        if (!this._underlay) {
            const bubbleUnderlayData = Game.design.getData(BURST_BUBBLE_IN_GAME_OVERLAY_1, DesignDataTypes.eGRAPHIC);
            this._underlay = new GameSprite(bubbleUnderlayData.asset);
        }

        if (!this._underlayBurst) {
            const bubbleUnderlayBurstData = Game.design.getData(BURST_BUBBLE_IN_GAME_OVERLAY_2, DesignDataTypes.eGRAPHIC);
            this._underlayBurst = new GameSprite(bubbleUnderlayBurstData.asset);
        }

        this._underlay.angle = 0;
        this._underlay.visible = true;
        this._underlayBurst.angle = 0;
        this._underlayBurst.visible = true;

        if (this._rotationTween) {
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        if (this._antiRotationTween) {
            this._antiRotationTween.destroy();
            this._antiRotationTween = null;
        }

        this._rotationTween = Game.windows
            .getCurrentWindow()
            .createTween(this._underlay)
            .to({ angle: 360 }, Common.settings.chameleonBubbleSpinTime)
            .repeat(Infinity)
            .start();

        this._antiRotationTween = Game.windows
            .getCurrentWindow()
            .createTween(this._underlayBurst)
            .to({ angle: -360 }, Common.settings.chameleonBubbleSpinTime)
            .repeat(Infinity)
            .start();
    }

    /**
     * @public
     */
    triggerBlooming() {
        this._animatedOverlay.visible = true;
        this._animatedOverlay.playAnimation("spawn");
        this._animatedOverlay.onComplete = () => {
            this._spawnBloomedBubbles();
        };
    }

    /**
     * @private
     */
    _spawnBloomedBubbles() {
        Game.fire(GameEvents.eGAME_SPAWN_BLOOMING, this);
        this._animatedOverlay.playAnimation("spawnEnd");
        this._animatedOverlay.onComplete = () => {
            this._animatedOverlay.visible = false;
        };
    }

    /**
     * @private
     */
    _tickDownCountdown() {
        if (!this.canCountdown) return;

        if (this._countdownLabel) {
            if (!this._countdownStarted) {
                this._countdownStarted = true;
                return;
            }
            --this._currentCountdown;
            this._countdownLabel.text = String(this._currentCountdown);
            if (this._currentCountdown <= 0) {
                Game.fire(GameEvents.eBUBBLE_COUNTDOWN_EFFECT, this);
            }
        }
    }

    _onDelayCountdown() {
        this._countdownStarted = false;
    }

    /**
     * @public
     */
    resetCountdown() {
        this._currentCountdown = this._countdown;
        if (this._countdownLabel) this._countdownLabel.text = String(this._currentCountdown);
    }

    /**
     * @private
     */
    _destroyTweens() {
        if (this._rotationTween) {
            this._rotationTween.destroy();
            this._rotationTween = null;
        }

        if (this._antiRotationTween) {
            this._antiRotationTween.destroy();
            this._antiRotationTween = null;
        }

        if (this._timerScatterDelay) {
            this._timerScatterDelay.destroy();
            this._timerScatterDelay = null;
        }

        if (this._scatterMovementTween) {
            this._scatterMovementTween.destroy();
            this._scatterMovementTween = null;
        }

        if (this._timerPopDelay) {
            this._timerPopDelay.destroy();
            this._timerPopDelay = null;
        }

        if (this._spawnTween) {
            this._spawnTween.destroy();
            this._spawnTween = null;
        }
    }

    /**
     * @public
     */
    destroy() {
        if (this._graphic) {
            this._graphic.destroy();
            this._graphic = null;
        }

        if (this._overlay) {
            this._overlay.destroy();
            this._overlay = null;
        }

        if (this._underlay) {
            this._underlay.destroy();
            this._underlay = null;
        }

        if (this._underlayBurst) {
            this._underlayBurst.destroy();
            this._underlayBurst = null;
        }

        if (this._colorChangeOverlay) {
            this._colorChangeOverlay.destroy();
            this._colorChangeOverlay = null;
        }

        if (this._countdownLabel) {
            this._countdownLabel.destroy();
            this._countdownLabel = null;
        }

        if (this._popAnimation) {
            this._popAnimation.visible = false;
            this._popAnimation.destroy();
            this._popAnimation = null;
        }

        if (this._animatedUnderlay) {
            this._animatedUnderlay.visible = false;
            this._animatedUnderlay.destroy();
            this._animatedUnderlay = null;
        }

        if (this._animatedOverlay) {
            this._animatedOverlay.visible = false;
            this._animatedOverlay.destroy();
            this._animatedOverlay = null;
        }

        if (this._cannonOutline) {
            this._cannonOutline.destroy();
            this._cannonOutline = null;
        }

        if (this._cannonSpin) {
            this._cannonSpin.destroy();
            this._cannonSpin = null;
        }

        if (this._root) {
            this._root.destroy();
            this._root = null;
        }

        this._destroyTweens();
        Game.off(GameEvents.eGAME_BUBBLE_DELAY_COUNTDOWN, this._onDelayCountdown, this);
        Game.off(GameEvents.eGAME_TICKDOWN_COUNTDOWN, this._tickDownCountdown, this);
    }
}
